// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import FileUpload, { FileUploadProps, FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { BaseFormFieldProps, FormFieldUploadedFileType, FormFieldValue } from "@store/Framework/Types/FormField";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormFieldUploadedFile from "@store/Library/Components/FormField/FormFieldUploadedFile";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import constants from "@store/constants";

type FieldValueType = FormFieldUploadedFileType[];
export type FileUploadFormFieldValue = FormFieldValue<FieldValueType>;

export interface FileUploadFormFieldPresentationProps extends ExpandStyles<FileUploadFormFieldStyles> {}

export interface FileUploadFormFieldProps
    extends Omit<FileUploadProps, keyof FileUploadPresentationProps | "defaultValue" | "error" | "value">,
        FileUploadFormFieldPresentationProps,
        BaseFormFieldProps<FieldValueType> {
    maxTotalSizeInBytes?: number;
    onRemoveFile?: (file: FormFieldUploadedFileType) => void;
    requiredErrorMessage?: string;
}

export interface FileUploadFormFieldStyles {
    filesWrapper?: InjectableCss;
    fileUpload?: FileUploadPresentationProps;
    wrapper?: InjectableCss;
}

export const fileUploadFormFieldStyles: FileUploadFormFieldStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    filesWrapper: {
        css: css`
            margin-top: ${pxToRem(8)};
        `,
    },
};

const FileUploadFormField: FC<FileUploadFormFieldProps> = ({
    accept,
    defaultValue = [],
    extendedStyles,
    label,
    maxTotalSizeInBytes = constants.maxPayloadSizeInBytes,
    onChange,
    onRemoveFile,
    onUpdate,
    onValidate,
    overridedStyles,
    required,
    requiredErrorMessage,
    showError = true,
    ...fileUploadProps
}) => {
    const styles = useExpandProps(fileUploadFormFieldStyles, extendedStyles, overridedStyles);
    const [value, setValue] = useState(defaultValue);
    const [error, setError] = useState("");
    const textLabel = typeof label === "string" ? label : undefined;
    const supportedExtensions = accept
        ?.split(",")
        .map(x => x.trim().toUpperCase())
        .filter(Boolean);

    const validate = (files: FormFieldUploadedFileType[]) => {
        let errorMessage: string | undefined;

        if (required && !files.length) {
            errorMessage =
                requiredErrorMessage || siteMessage("Field_Required", textLabel || translate("This field")).toString();
        }

        if (
            supportedExtensions?.length &&
            files.some(file => !supportedExtensions.some(ext => file.fileName.toUpperCase().endsWith(ext)))
        ) {
            errorMessage = siteMessage(
                "FileUploadFormField_UnsupportedType",
                supportedExtensions.join(", "),
            ).toString();
        }

        if (!errorMessage && maxTotalSizeInBytes) {
            const totalSizeInBytes = files.reduce((total, file) => total + file.size, 0);

            if (totalSizeInBytes > maxTotalSizeInBytes) {
                const maxSizeInMb = Math.floor(maxTotalSizeInBytes / (1024 * 1024)).toString();
                errorMessage = siteMessage("FileUploadFormField_TotalSizeExceeded", maxSizeInMb).toString();
            }
        }

        errorMessage = onValidate?.({ isValid: !errorMessage, value: files }) ?? errorMessage;

        setError(errorMessage || "");

        return !errorMessage;
    };

    useEffect(() => {
        const isValid = validate(value);
        onUpdate?.({ isValid, value });
    }, []);

    const removeFile = (file: FormFieldUploadedFileType) => {
        const remainingFiles = value.filter(
            x => !(x.fileName === file.fileName && x.fileType === file.fileType && x.size === file.size),
        );

        setValue(remainingFiles);
        const isValid = validate(remainingFiles);
        onUpdate?.({ isValid, value: remainingFiles });
        onRemoveFile?.(file);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <FileUpload
                {...styles.fileUpload}
                placeholder={`${translate("Upload your documents")}${supportedExtensions ? ` (${supportedExtensions.join(", ")})` : ""}`}
                {...fileUploadProps}
                accept={accept}
                error={showError ? error : ""}
                label={label || textLabel}
                multiple
                onChange={async event => {
                    onChange?.(event);
                    const uploadedFiles = await Promise.all(
                        Array.from(event.target.files || []).map(
                            file =>
                                new Promise<FormFieldUploadedFileType>((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                        const data = (reader.result as string).split(",")[1];
                                        resolve({
                                            fileName: file.name,
                                            fileType: file.type,
                                            size: data.length,
                                            data,
                                        });
                                    };
                                    reader.onerror = reject;
                                    reader.readAsDataURL(file);
                                }),
                        ),
                    );

                    const mergedFiles = [...value, ...uploadedFiles].filter(
                        (file, index, allFiles) =>
                            allFiles.findIndex(x => x.fileName === file.fileName && x.size === file.size) === index,
                    );

                    setValue(mergedFiles);
                    const isValid = validate(mergedFiles);
                    onUpdate?.({ isValid, value: mergedFiles });
                    event.target.value = "";
                }}
                required={required}
            />
            {value.length > 0 && (
                <StyledWrapper {...styles.filesWrapper}>
                    {value.map((file, index) => (
                        <FormFieldUploadedFile
                            file={file}
                            key={`${file.fileName}-${index}`}
                            onRemoveClick={() => removeFile(file)}
                        />
                    ))}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default FileUploadFormField;
